import React, { useState, useEffect, useRef } from "react";
import { Search, Loader2 } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { CONTRACT_TYPES, DEPARTMENTS, SPEND_CATEGORIES, CURRENCIES } from "./WizardConstants";

export function WizardStepDetails({ form, set, supplierState }) {
  const { supplierQuery, setSupplierQuery, supplierResults, setSupplierResults,
          supplierSearching, setSupplierSearching, supplierDropdownOpen, setSupplierDropdownOpen,
          supplierConfirmed, setSupplierConfirmed, selectSupplier } = supplierState;
  const supplierRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (supplierRef.current && !supplierRef.current.contains(e.target)) setSupplierDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [setSupplierDropdownOpen]);

  const searchSuppliers = (query) => {
    setSupplierQuery(query);
    set("contracting_party.legal_name", query);
    setSupplierConfirmed(false);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (query.length < 1) { setSupplierResults([]); setSupplierDropdownOpen(false); return; }
    searchTimeoutRef.current = setTimeout(async () => {
      setSupplierSearching(true);
      try {
        const res = await apiClient.get(`/suppliers/search?q=${encodeURIComponent(query)}`);
        setSupplierResults(res.data || []);
        setSupplierDropdownOpen(res.data.length > 0);
      } catch { /* silent */ }
      finally { setSupplierSearching(false); }
    }, 300);
  };

  return (
    <div className="space-y-5" data-testid="wizard-step-details-content">
      <div>
        <h2 className="text-lg font-medium">Contract & Supplier Details</h2>
        <p className="text-sm text-slate-500 mt-1">Enter the core information about this contract.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2 space-y-1">
          <Label>Contract Title *</Label>
          <Input value={form.title} onChange={e => set("title", e.target.value)} placeholder="e.g., IT Support Services Agreement 2026" className="rounded-sm" data-testid="wizard-title" />
        </div>
        <div className="space-y-1">
          <Label>Contract Type *</Label>
          <Select value={form.contract_type} onValueChange={v => set("contract_type", v)}>
            <SelectTrigger className="rounded-sm" data-testid="wizard-type"><SelectValue placeholder="Select type" /></SelectTrigger>
            <SelectContent>{CONTRACT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>Contract Value</Label>
          <div className="flex gap-2">
            <Select value={form.currency} onValueChange={v => set("currency", v)}>
              <SelectTrigger className="rounded-sm w-28" data-testid="wizard-currency"><SelectValue placeholder="Currency *" /></SelectTrigger>
              <SelectContent className="max-h-60">{CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="number" value={form.contract_value} onChange={e => set("contract_value", e.target.value)} placeholder="e.g., 50000" className="rounded-sm flex-1" data-testid="wizard-value" />
          </div>
        </div>
        <div className="space-y-1">
          <Label>Effective Date</Label>
          <Input type="date" value={form.effective_date} onChange={e => set("effective_date", e.target.value)} className="rounded-sm" data-testid="wizard-effective" />
        </div>
        <div className="space-y-1">
          <Label>Expiration Date</Label>
          <Input type="date" value={form.expiration_date} onChange={e => set("expiration_date", e.target.value)} className="rounded-sm" data-testid="wizard-expiration" />
        </div>
        <div className="space-y-1">
          <Label>Department</Label>
          <Select value={form.department} onValueChange={v => set("department", v)}>
            <SelectTrigger className="rounded-sm" data-testid="wizard-department"><SelectValue placeholder="Select department" /></SelectTrigger>
            <SelectContent>{DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>Spend Category</Label>
          <Select value={form.spend_category} onValueChange={v => set("spend_category", v)}>
            <SelectTrigger className="rounded-sm" data-testid="wizard-spend-category"><SelectValue placeholder="Select category" /></SelectTrigger>
            <SelectContent>{SPEND_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="border-t pt-4">
        <h3 className="text-sm font-medium mb-3">Supplier / Counterparty *</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2 space-y-1 relative" ref={supplierRef}>
            <Label>Legal Name *</Label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={supplierQuery || form.contracting_party.legal_name} onChange={e => searchSuppliers(e.target.value)} onFocus={() => { if (supplierResults.length > 0) setSupplierDropdownOpen(true); }} placeholder="Search existing suppliers or type a new name..." className="rounded-sm pl-9" data-testid="wizard-supplier-name" />
              {supplierSearching && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />}
            </div>
            {supplierDropdownOpen && supplierResults.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-900 border rounded-sm shadow-lg max-h-48 overflow-y-auto" data-testid="supplier-search-results">
                {supplierResults.map((s, i) => (
                  <button key={`${s.name}-${i}`} className="w-full text-left px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800 border-b last:border-0 transition-colors" onClick={() => selectSupplier(s)} data-testid={`supplier-option-${i}`}>
                    <p className="text-sm font-medium">{s.name}</p>
                    <p className="text-[10px] text-slate-400">{s.supplier_id && <span className="font-mono text-cyan-600">{s.supplier_id} · </span>}{s.email && `${s.email} · `}{s.phone && `${s.phone} · `}{s.contract_count} contract{s.contract_count !== 1 ? "s" : ""}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-1">
            <Label>Registered Address</Label>
            <Input value={form.contracting_party.registered_address} onChange={e => set("contracting_party.registered_address", e.target.value)} placeholder="Full address" className="rounded-sm" />
          </div>
          <div className="space-y-1">
            <Label>Contact Email *</Label>
            <Input type="email" value={form.contracting_party.contact_email} onChange={e => set("contracting_party.contact_email", e.target.value)} placeholder="supplier@company.com" className="rounded-sm" data-testid="wizard-supplier-email" />
          </div>
          <div className="space-y-1">
            <Label>Contact Phone *</Label>
            <Input value={form.contracting_party.contact_phone} onChange={e => set("contracting_party.contact_phone", e.target.value)} placeholder="+44 xxx xxx xxxx" className="rounded-sm" data-testid="wizard-supplier-phone" />
          </div>
          <div className="space-y-1">
            <Label>Supplier ID</Label>
            <Input value={form.supplier_unique_id} onChange={e => set("supplier_unique_id", e.target.value)} placeholder="e.g., SUP-001" className="rounded-sm" data-testid="wizard-supplier-id" />
            <p className="text-[10px] text-slate-400">{form.supplier_unique_id ? "Auto-filled from existing supplier" : "Enter a unique identifier"}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
